import * as Sentry from '@sentry/react';
import { MENU_OBJECT_TYPES } from 'constants/menuManagement';
import { uploadImage } from 'helpers/menuManagement';
import { toAnalyticsEntityType, trackMenuEntityAction } from 'helpers/menuAnalytics';
import { normalizeServingsTag } from 'components/MenuManagement/validation';
import {
  FormValues,
  FormSubmissionConfig,
  UploadImageResponse,
  NewItemDefaults,
} from 'components/MenuManagement/MenuItemActions/types/newDrawerTypes';
import type { StagedImageFile } from 'components/MenuManagement/MenuEdit/Fields';

// The backend reads item_uuid from the multipart Content-Disposition filename.
// The item must already exist in Cuboh before this upload is called.
async function uploadPhotoForNewItem(
  file: StagedImageFile,
  itemUuid: string,
  companyId: number,
  itemType: string,
  setUploading: (isUploading: boolean) => void,
): Promise<string | undefined> {
  setUploading(true);
  try {
    const renamedFile = new File([file.originFileObj], itemUuid, {
      type: file.originFileObj.type || 'image/jpeg',
    });
    const response = (await uploadImage({
      companyId,
      itemType,
      uploadImageFile: { name: itemUuid, originFileObj: renamedFile },
      imageEnhancedAt: file.imageEnhancedAt,
    })) as UploadImageResponse;
    return response?.file?.url;
  } finally {
    setUploading(false);
  }
}

function formatRecord(
  values: FormValues,
  defaults: NewItemDefaults,
  hasClassifications: boolean,
  requiresPrices: boolean,
  price: number | null,
  imageUrl?: string,
) {
  const formattedClassifications = values.classifications.filter(
    classification => !classification.isDeleted,
  );

  return {
    ...defaults,
    ...values,
    servingsTag: normalizeServingsTag(values.servingsTag),
    ...(requiresPrices && {
      price: values.price ?? price,
    }),
    ...(hasClassifications && {
      classifications: formattedClassifications,
    }),
    ...(imageUrl && {
      imageUrl,
    }),
    ...(defaults.itemType === MENU_OBJECT_TYPES.modifiers && {
      isDefault: values.isDefault === 'true',
    }),
    individualMinimum: values.individualMinimum,
    individualMaximum: values.individualMaximum,
  };
}

export function formSubmitHandler({
  close,
  createNewCubohItem,
  menuId,
  newItemDefaults,
  newItemHasClassifications,
  newItemPrice,
  pricesRequired,
  resetFields,
  uploadImageFile,
  setIsSubmitting,
  setIsUploading,
  refetchPhotos,
  handleErrorNotification,
  handleSuccessNotification,
}: FormSubmissionConfig) {
  return async function handleFormSubmission(values: FormValues): Promise<void> {
    setIsSubmitting(true);
    try {
      // 1. Create the item first (without imageUrl).
      // The Cuboh file-upload endpoint requires the item to already exist — it reads
      // item_uuid from the multipart filename and 404s if no match is found.
      const initialRecord = formatRecord(
        values,
        newItemDefaults,
        newItemHasClassifications,
        pricesRequired,
        newItemPrice,
      );

      const response = await createNewCubohItem({
        group: newItemDefaults.itemType,
        data: initialRecord,
      });

      const newItemUuid = response?.uuid;

      trackMenuEntityAction({
        action: 'create',
        entityType: toAnalyticsEntityType(newItemDefaults.itemType),
        menuId,
        entityId: newItemUuid ?? null,
      });

      // 2. Upload the staged photo with the new item's UUID.
      // The backend associates the photo with the item on upload (reads item_uuid from the
      // multipart filename), so no separate update call is needed after a successful upload.
      if (uploadImageFile && newItemUuid) {
        await uploadPhotoForNewItem(
          uploadImageFile,
          newItemUuid,
          newItemDefaults.companyId,
          newItemDefaults.itemType,
          setIsUploading,
        );
        refetchPhotos();
      }

      handleSuccessNotification({
        parentUuid: newItemDefaults.parentUuid,
        customMessage: `Successfully added ${values.name}`,
      });

      resetFields();
      close();
    } catch (error: unknown) {
      setIsUploading(false);
      if (error instanceof Error) {
        if (error.message === 'Network Error') {
          handleErrorNotification({
            customMessage: `We could not add ${values.name}. Please check your connection and try again.`,
          });
          return;
        }

        error.name = '[MENU MANAGEMENT] Failed to create new item';
        Sentry.captureException(error, {
          extra: {
            item_name: values.name,
            error_message: error.message,
          },
        });
      }

      handleErrorNotification({
        customMessage: `We could not add ${values.name}. Please contact customer support.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
}
